import moment from "moment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UpdateIcon from "@mui/icons-material/Update";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

function ConfirmationStep({ logic, stock }) {
  // Identificar produtos novos vs. existentes
  const getProductStatus = (item) => {
    if (!stock || !stock.stock) return "new";

    const isExisting = stock.stock.some(
      (st) => st.produto.nome.toLowerCase() === item.nome.toLowerCase(),
    );

    return isExisting ? "update" : "new";
  };

  // Calcular estatísticas
  const newProducts = logic.processedData.filter(
    (item) => getProductStatus(item) === "new",
  );

  const updatedProducts = logic.processedData.filter(
    (item) => getProductStatus(item) === "update",
  );

  const totalQuantity = logic.processedData.reduce(
    (sum, item) => sum + Number(item.quantidade),
    0,
  );

  const totalCost = logic.custoTotal;

  // Calcular estatísticas para os novos campos
  const materiaPrimaCount = logic.processedData.filter(
    (item) => item.classificacao === "MP",
  ).length;

  const produtoAcabadoCount = logic.processedData.filter(
    (item) => item.classificacao === "PA" || !item.classificacao,
  ).length;

  const withPriceCount = logic.processedData.filter(
    (item) => item.precoCompra && item.precoCompra > 0,
  ).length;

  const withFornecedorCount = logic.processedData.filter(
    (item) => item.fornecedor && item.fornecedor !== "",
  ).length;

  const getNomeLocalizacao = (armazemId, localizacaoId) => {
    if (!armazemId || !localizacaoId) return "Não definida";
    const arm = (logic.armazensListImport || []).find(
      (a) => a.id === armazemId,
    );
    const loc = (logic.localizacoesList || []).find(
      (l) => l.id === localizacaoId,
    );
    if (!arm && !loc) return "Não definida";
    return `${arm?.codigo || "?"}/${loc?.codigo || "?"} — ${loc?.nome || ""}`;
  };

  return (
    <div className="enhanced-import-step confirmation-step">
      <div className="confirmation-header">
        <h3>Confirmação da Importação</h3>
        <p>Revise os dados e confirme para finalizar a importação.</p>
      </div>

      <div className="confirmation-summary">
        <h4>Resumo da Importação</h4>

        <div className="confirmation-summary-grid">
          <div className="confirmation-summary-section">
            <h5>Quantidades</h5>
            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">
                Total de produtos:
              </div>
              <div className="confirmation-summary-value">
                {logic.processedData.length}
              </div>
            </div>

            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">
                Novos produtos a criar:
              </div>
              <div className="confirmation-summary-value">
                {newProducts.length}
              </div>
            </div>

            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">
                Produtos existentes a atualizar:
              </div>
              <div className="confirmation-summary-value">
                {updatedProducts.length}
              </div>
            </div>

            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">
                Quantidade total a adicionar:
              </div>
              <div className="confirmation-summary-value">{totalQuantity}</div>
            </div>
          </div>

          <div className="confirmation-summary-section">
            <h5>Classificação</h5>
            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">Matéria Prima:</div>
              <div className="confirmation-summary-value">
                {materiaPrimaCount}
                <span className="confirmation-summary-percentage">
                  (
                  {(
                    (materiaPrimaCount / logic.processedData.length) *
                    100
                  ).toFixed(1)}
                  %)
                </span>
              </div>
            </div>

            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">Produto Acabado:</div>
              <div className="confirmation-summary-value">
                {produtoAcabadoCount}
                <span className="confirmation-summary-percentage">
                  (
                  {(
                    (produtoAcabadoCount / logic.processedData.length) *
                    100
                  ).toFixed(1)}
                  %)
                </span>
              </div>
            </div>
          </div>

          <div className="confirmation-summary-section">
            <h5>Preços e Fornecedores</h5>
            {totalCost > 0 && (
              <div className="confirmation-summary-item">
                <div className="confirmation-summary-label">Valor total:</div>
                <div className="confirmation-summary-value confirmation-summary-value-cost">
                  {totalCost.toFixed(2)} CVE
                </div>
              </div>
            )}

            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">
                Produtos com preço definido:
              </div>
              <div className="confirmation-summary-value">
                {withPriceCount}
                <span className="confirmation-summary-percentage">
                  (
                  {(
                    (withPriceCount / logic.processedData.length) *
                    100
                  ).toFixed(1)}
                  %)
                </span>
              </div>
            </div>

            <div className="confirmation-summary-item">
              <div className="confirmation-summary-label">
                Produtos com fornecedor:
              </div>
              <div className="confirmation-summary-value">
                {withFornecedorCount}
                <span className="confirmation-summary-percentage">
                  (
                  {(
                    (withFornecedorCount / logic.processedData.length) *
                    100
                  ).toFixed(1)}
                  %)
                </span>
              </div>
            </div>
          </div>
        </div>

        {logic.adicionarComoDespesa && (
          <div className="confirmation-summary-item confirmation-summary-expense">
            <div className="confirmation-summary-label">
              Despesa a registrar:
            </div>
            <div className="confirmation-summary-value">
              {logic.descricao || "Compra de produtos para stock"}(
              {logic.custoTotal.toFixed(2)}CVE)
              {logic.despesaPaga ? " - Já paga" : " - A pagar"}
            </div>
          </div>
        )}
      </div>

      <div className="confirmation-details">
        <h4>Detalhes dos Produtos</h4>
        <div className="confirmation-table-container">
          <table className="confirmation-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Qtd</th>
                <th>Lote</th>
                <th>Validade</th>
                <th>Preço</th>
                <th>Fornecedor</th>
                <th>Classificação</th>
                <th>Armazém/Local</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {logic.processedData.map((item, index) => {
                const isExisting = getProductStatus(item) === "update";

                return (
                  <tr
                    key={index}
                    className={`confirmation-row ${
                      isExisting
                        ? "confirmation-row__existing"
                        : "confirmation-row__new"
                    }`}
                  >
                    <td>{item.nome}</td>
                    <td>{item.quantidade}</td>
                    <td>{item.lote}</td>
                    <td>
                      {item.dataValidade
                        ? moment(item.dataValidade).format("DD/MM/YYYY")
                        : "-"}
                    </td>
                    <td>
                      {item.precoCompra && item.precoCompra > 0
                        ? `${item.precoCompra.toFixed(2)} CVE`
                        : "-"}
                    </td>
                    <td>
                      {item.fornecedor ? (
                        <Chip
                          size="small"
                          label={item.fornecedorNome || "Fornecedor"}
                          className="confirmation-fornecedor-chip"
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <Chip
                        size="small"
                        label={
                          item.classificacao === "MP"
                            ? "Matéria Prima"
                            : "Produto Acabado"
                        }
                        className={`confirmation-classificacao-chip ${
                          item.classificacao === "MP"
                            ? "confirmation-classificacao-chip-materia"
                            : "confirmation-classificacao-chip-produto"
                        }`}
                      />
                    </td>
                    <td>
                      <Tooltip title="Localização física no estoque">
                        <div className="confirmation-location">
                          {getNomeLocalizacao(item.armazem, item.localizacao)}
                        </div>
                      </Tooltip>
                    </td>
                    <td>
                      <div
                        className={`confirmation-status ${
                          isExisting ? "update" : "create"
                        }`}
                      >
                        {isExisting ? (
                          <>
                            <UpdateIcon className="confirmation-status-icon" />
                            <span>Atualizar</span>
                          </>
                        ) : (
                          <>
                            <AddCircleIcon className="confirmation-status-icon" />
                            <span>Criar novo</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="confirmation-notice">
        <CheckCircleIcon className="confirmation-notice-icon" />
        <p>
          Ao confirmar, os produtos serão adicionados ao estoque e, se
          configurada, uma despesa será registrada no sistema financeiro. Esta
          ação não pode ser desfeita.
        </p>
      </div>
    </div>
  );
}

export default ConfirmationStep;
